import {
  indexNodeLicensesBaseUrl,
  NODE_STORAGE_SHARE_TOKEN_HEADER,
  type NodeStorageUploadResult,
  type PublicNodeStorageShareSummary
} from 'state/services/indexNode/indexNode'

function shareAuthHeaders(token: string): Record<string, string> {
  return { [NODE_STORAGE_SHARE_TOKEN_HEADER]: token }
}

export function buildPublicNodeStorageShareUrl(token: string): string {
  if (typeof window === 'undefined') {
    return `/nodes-storage/shared?t=${encodeURIComponent(token)}`
  }
  return `${window.location.origin}/nodes-storage/shared?t=${encodeURIComponent(token)}`
}

export async function downloadPublicShareFile(params: {
  token: string
  path: string
  filenameHint?: string
}): Promise<void> {
  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const url = `${base}/public/node-storage/file?path=${encodeURIComponent(params.path)}`
  const res = await fetch(url, { headers: shareAuthHeaders(params.token) })
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`)
  }
  const blob = await res.blob()
  const a = document.createElement('a')
  const href = URL.createObjectURL(blob)
  a.href = href
  a.download = params.filenameHint ?? params.path.split('/').filter(Boolean).pop() ?? 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

export async function uploadPublicShareSmallFile(params: {
  token: string
  file: File
  relativePath: string
}): Promise<NodeStorageUploadResult> {
  const form = new FormData()
  form.append('file', params.file)
  form.append('relativePath', params.relativePath)
  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const res = await fetch(`${base}/public/node-storage/upload`, {
    method: 'POST',
    headers: shareAuthHeaders(params.token),
    body: form
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Upload failed (${res.status})`)
  }
  return (await res.json()) as NodeStorageUploadResult
}

export async function uploadPublicShareFileChunked(params: {
  token: string
  file: File
  relativePath: string
  summary: PublicNodeStorageShareSummary
}): Promise<NodeStorageUploadResult> {
  const { token, file, relativePath, summary } = params
  const chunkSize =
    summary.chunkUpload?.recommendedChunkBytes ?? Math.floor(4_194_304 * 0.85)
  if (chunkSize < 1024) {
    throw new Error('Invalid chunk size from server')
  }

  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const jsonHeaders = {
    ...shareAuthHeaders(token),
    'Content-Type': 'application/json'
  }

  const startRes = await fetch(`${base}/public/node-storage/chunks/start`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({
      relativePath,
      totalSize: file.size,
      chunkSize
    })
  })
  if (!startRes.ok) {
    throw new Error(`Chunk session failed: ${startRes.status}`)
  }
  const { uploadId, expectedChunks } = (await startRes.json()) as {
    uploadId: string
    expectedChunks: number
  }

  const totalChunks = Math.ceil(file.size / chunkSize)
  if (totalChunks !== expectedChunks) {
    throw new Error('Chunk count mismatch')
  }

  for (let i = 0; i < totalChunks; i++) {
    const startB = i * chunkSize
    const endB = Math.min(startB + chunkSize, file.size)
    const blob = file.slice(startB, endB)
    const form = new FormData()
    form.append('chunk', blob, `part-${i}`)
    const putRes = await fetch(`${base}/public/node-storage/chunks/${uploadId}/${i}`, {
      method: 'PUT',
      headers: shareAuthHeaders(token),
      body: form
    })
    if (!putRes.ok) {
      throw new Error(`Chunk ${i} failed: ${putRes.status}`)
    }
  }

  const completeRes = await fetch(`${base}/public/node-storage/chunks/${uploadId}/complete`, {
    method: 'POST',
    headers: shareAuthHeaders(token)
  })
  if (!completeRes.ok) {
    throw new Error(`Chunk complete failed: ${completeRes.status}`)
  }
  return (await completeRes.json()) as NodeStorageUploadResult
}

export async function deletePublicShareItem(params: { token: string; path: string }): Promise<void> {
  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const res = await fetch(
    `${base}/public/node-storage/file?path=${encodeURIComponent(params.path)}`,
    {
      method: 'DELETE',
      headers: shareAuthHeaders(params.token)
    }
  )
  if (!res.ok) {
    throw new Error(`Delete failed (${res.status})`)
  }
}

export async function mkdirPublicShareFolder(params: {
  token: string
  path: string
}): Promise<{ path: string }> {
  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const res = await fetch(`${base}/public/node-storage/mkdir?path=${encodeURIComponent(params.path)}`, {
    method: 'POST',
    headers: shareAuthHeaders(params.token)
  })
  if (!res.ok) {
    throw new Error(`Mkdir failed (${res.status})`)
  }
  return (await res.json()) as { path: string }
}

export async function movePublicShareItem(params: {
  token: string
  from: string
  to: string
}): Promise<{ ok: boolean; from: string; to: string }> {
  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const q = new URLSearchParams({ from: params.from, to: params.to })
  const res = await fetch(`${base}/public/node-storage/move?${q.toString()}`, {
    method: 'POST',
    headers: shareAuthHeaders(params.token)
  })
  if (!res.ok) {
    throw new Error(`Move failed (${res.status})`)
  }
  return (await res.json()) as { ok: boolean; from: string; to: string }
}
